import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Brain, Cloud, Palette, Smartphone, Shield, ArrowUpRight } from 'lucide-react';

const features = [
  {
    icon: Code2,
    title: 'Custom Software Development',
    description: 'Tailored software solutions designed to meet your specific business requirements and drive growth.',
    accent: 'gold',
  },
  {
    icon: Brain,
    title: 'Web Based Applications',
    description: 'Modern web applications with intuitive interfaces and powerful functionality for seamless user experience.',
    accent: 'green',
    highlighted: true,
  },
  {
    icon: Cloud,
    title: 'ERP Solutions',
    description: 'Comprehensive enterprise resource planning systems to streamline your business operations.',
    accent: 'teal',
  },
  {
    icon: Palette,
    title: 'Database Systems',
    description: 'Robust database design and management solutions for efficient data handling and storage.',
    accent: 'gold',
  },
  {
    icon: Smartphone,
    title: 'Business Automation Systems',
    description: 'Automated workflows and processes to increase efficiency and reduce manual tasks.',
    accent: 'teal',
  },
  {
    icon: Shield,
    title: 'Financial / Stock Market Solutions',
    description: 'Specialized software for financial services, trading platforms, and market analysis tools.',
    accent: 'gold',
  },
];

const Features = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0e3a63]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-white">Your Partner in</span>{' '}
            <span className="text-[#EFCD86]">Digital Excellence</span>
          </h2>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Comprehensive software solutions tailored for modern businesses, from web applications to enterprise systems.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isHighlighted = feature.highlighted;
            
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`group relative rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                  isHighlighted
                    ? 'bg-[#66B152] text-white shadow-xl shadow-[#66B152]/30'
                    : 'bg-[#0588b3] border border-[#046A8C]/20 hover:border-[#EFCD86]/50 hover:shadow-lg hover:shadow-[#EFCD86]/10'
                }`}
              >
                {/* Top Row: Icon and Arrow */}
                <div className="flex justify-between items-start mb-4">
                  <div className={`p-3 rounded-lg ${
                    isHighlighted 
                      ? 'bg-white/20' 
                      : feature.accent === 'gold' 
                        ? 'bg-[#EFCD86]/10' 
                        : 'bg-[#046A8C]/10'
                  }`}>
                    <Icon 
                      size={24} 
                      className={isHighlighted ? 'text-white' : feature.accent === 'gold' ? 'text-[#EFCD86]' : 'text-[#046A8C]'} 
                    />
                  </div>
                  
                  <div className={`p-2 rounded-full transition-transform duration-300 group-hover:rotate-45 ${
                    isHighlighted ? 'bg-white/20' : 'bg-white/5'
                  }`}>
                    <ArrowUpRight size={18} className={isHighlighted ? 'text-white' : 'text-gray-400'} />
                  </div>
                </div>

                {/* Content */}
                <h3 className={`text-xl font-bold mb-2 ${isHighlighted ? 'text-white' : 'text-white'}`}>
                  {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed ${isHighlighted ? 'text-white/90' : 'text-white/90'}`}>
                  {feature.description}
                </p>

                {/* Hover Glow Effect for non-highlighted cards */}
                {!isHighlighted && (
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: feature.accent === 'gold' 
                        ? 'radial-gradient(circle at 50% 0%, rgba(239, 205, 134, 0.1), transparent 70%)'
                        : 'radial-gradient(circle at 50% 0%, rgba(4, 106, 140, 0.1), transparent 70%)'
                    }}
                  />
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
